import get from 'lodash/get';

import { DEFAULT_BOOT_MODE } from '../../../capabilities/bare-metal-provisioning';
import { deserializeServiceListFromApi } from '../../../capabilities/service/serviceApiField';
import type { PipelineContext } from '../../../core/types';
import { buildSystemOptionsFromForm } from '../../../platform/container-deploy-config';
import { buildDefaultDirectResourceModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import { hasMeaningfulValue } from '../../../utils';
import { resolveEnabledModulesForSpecFilter } from '../../_shared/common/sku/resolveEnabledModulesForSpecFilter';
import { filterBareMetalSpecByEnabledModules } from './enabledModules';
import type { BareMetalSpec } from './schema';

function readOptionalString(value: unknown): string | undefined {
  if (!hasMeaningfulValue(value)) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function resolveProvisioningMode(values: Record<string, unknown>): 'Install' | 'Adopt' {
  const mode = readOptionalString(get(values, 'provisioning.mode'));
  return mode === 'Adopt' ? 'Adopt' : 'Install';
}

export function bareMetalFromForm(values: Record<string, unknown>, ctx: PipelineContext): BareMetalSpec {
  const image = get(values, 'image', {}) as Record<string, unknown>;
  const optionMeta = get(values, 'optionMeta');
  const network = values.network as Record<string, unknown> | undefined;
  const provisioningMode = resolveProvisioningMode(values);
  const isAdopt = provisioningMode === 'Adopt';

  const vpcName = readOptionalString(get(values, 'vpc.name'));
  const subnetName = readOptionalString(get(values, 'subnet.name'));
  const securitygroupName = readOptionalString(get(values, 'securitygroup.name'));
  const staticIpv4 = readOptionalString(values.staticIpv4 ?? get(values, 'network.staticIpv4'));

  const systemOptions = buildSystemOptionsFromForm(values);
  const bootMode = readOptionalString(get(values, 'boot.mode')) || DEFAULT_BOOT_MODE;

  const adoptArchitecture = readOptionalString(get(values, 'systemVolume.architecture'));
  const adoptOs = readOptionalString(get(values, 'systemVolume.os'));
  const adoptVersion = readOptionalString(get(values, 'systemVolume.version'));
  const accountPasswd = readOptionalString(get(values, 'account.passwd'));
  const accountPrivatedKey = readOptionalString(get(values, 'account.privatedKey'));

  const spec: BareMetalSpec = {
    name: String(values.name ?? ''),
    description: readOptionalString(values.description),
    workspace: readOptionalString(values.workspace),
    datacenter: readOptionalString(values.datacenter),
    ...(optionMeta && typeof optionMeta === 'object' && !Array.isArray(optionMeta)
      ? { optionMeta: optionMeta as Record<string, unknown> }
      : {}),
    bmsNode: readOptionalString(values.bmsNode),
    powerPolicy: readOptionalString(values.powerPolicy),
    provisioning: { mode: provisioningMode },
    boot: { mode: bootMode },
    network: {
      vpc: vpcName ? { name: vpcName } : undefined,
      subnet: subnetName ? { name: subnetName } : undefined,
      securitygroup: securitygroupName ? { name: securitygroupName } : undefined,
      ...(isAdopt && staticIpv4 ? { staticIpv4 } : {}),
      services: Array.isArray(network?.service) ? (network.service as BareMetalSpec['network']['services']) : undefined,
    },
    systemVolume: isAdopt
      ? {
          diskType: 'physical',
          architecture: adoptArchitecture,
          os: adoptOs,
          version: adoptVersion,
        }
      : {
          image: readOptionalString(image.url ?? image.key),
          os: readOptionalString(image.os),
          version: readOptionalString(image.version),
          architecture: readOptionalString(image.architecture),
          kernel: readOptionalString(image.kernel),
          initrd: readOptionalString(image.initrd),
          root: readOptionalString(image.root),
        },
    ...(isAdopt && (accountPasswd || accountPrivatedKey)
      ? {
          account: {
            user: [
              {
                name: 'root',
                groups: ['root'],
                sudo: 'true',
                ...(accountPasswd ? { passwd: accountPasswd } : {}),
                ...(accountPrivatedKey ? { sshKey: { privatedKey: accountPrivatedKey } } : {}),
              },
            ],
          },
        }
      : {}),
    ...(systemOptions ? { system: { options: systemOptions } } : {}),
  };

  const enabledModules = resolveEnabledModulesForSpecFilter(ctx, values);
  return filterBareMetalSpecByEnabledModules(spec, enabledModules, ctx.sku?.skuType) as BareMetalSpec;
}

export function bareMetalToForm(spec: BareMetalSpec, _ctx?: PipelineContext): Record<string, unknown> {
  const mode = spec.provisioning?.mode === 'Adopt' ? 'Adopt' : 'Install';
  const rootUser = spec.account?.user?.find((item) => item.name === 'root') ?? spec.account?.user?.[0];

  return {
    name: spec.name,
    description: spec.description,
    workspace: spec.workspace,
    datacenter: spec.datacenter,
    ...(spec.optionMeta ? { optionMeta: spec.optionMeta } : {}),
    bmsNode: spec.bmsNode,
    powerPolicy: spec.powerPolicy,
    'provisioning.mode': mode,
    'boot.mode': spec.boot?.mode || DEFAULT_BOOT_MODE,
    'vpc.name': spec.network.vpc?.name,
    'subnet.name': spec.network.subnet?.name,
    'securitygroup.name': spec.network.securitygroup?.name,
    ...(spec.network.staticIpv4 ? { staticIpv4: spec.network.staticIpv4 } : {}),
    network: {
      service: deserializeServiceListFromApi(spec.network.services as unknown[] | undefined) ?? [],
    },
    ...(mode === 'Adopt'
      ? {
          'systemVolume.architecture': spec.systemVolume?.architecture,
          'systemVolume.os': spec.systemVolume?.os,
          'systemVolume.version': spec.systemVolume?.version,
          'account.passwd': rootUser?.passwd,
          'account.privatedKey': rootUser?.sshKey?.privatedKey,
        }
      : {
          image: {
            key: spec.systemVolume?.image,
            url: spec.systemVolume?.image,
            os: spec.systemVolume?.os,
            version: spec.systemVolume?.version,
            architecture: spec.systemVolume?.architecture,
            kernel: spec.systemVolume?.kernel,
            initrd: spec.systemVolume?.initrd,
            root: spec.systemVolume?.root,
          },
        }),
    ...(spec.system?.options ? { system: { options: spec.system.options } } : {}),
  };
}

export function bareMetalGetInitialFormValues(ctx?: PipelineContext): Record<string, unknown> {
  const datacenter = ctx?.cluster;
  return {
    ...(datacenter ? { datacenter } : {}),
    skuResourceModules: buildDefaultDirectResourceModules('BareMetal'),
    'subnet.name': 'default',
    'provisioning.mode': 'Install',
    'boot.mode': DEFAULT_BOOT_MODE,
    network: {
      service: [],
    },
  };
}
